import { lineAmountCents } from "./payments";
import { z } from "zod";

/**
 * QuickBooks Online payloads (spec §11): pure builders, so what would be sent can be tested and
 * shown before writes are turned on. Amounts go from cents to dollars only here.
 * A reference to something not in QuickBooks yet is "pending:<what>" (shown in the dry-run outbox).
 */

/** Cents → dollars with two decimals, exactly. */
export function qbAmount(cents: number): number {
  if (!Number.isInteger(cents)) throw new Error("Amounts must be whole cents.");
  return Number((cents / 100).toFixed(2));
}

/** QuickBooks display names: at most 100 characters, no ":" (it separates parent and sub-customer), no tabs or newlines. */
export function qbName(name: string): string {
  return name.replace(/[:\t\n\r]/g, " ").replace(/\s+/g, " ").trim().slice(0, 100);
}

type Ref = { value: string; name?: string };
const ref = (value: string, name?: string): Ref => (name ? { value, name } : { value });

export interface QbAddress {
  street: string;
  street2: string;
  city: string;
  state: string;
  zip: string;
}

const addr = (a: QbAddress) => (a.street || a.city ? { Line1: a.street, ...(a.street2 ? { Line2: a.street2 } : {}), City: a.city, CountrySubDivisionCode: a.state, PostalCode: a.zip } : undefined);

export function customerPayload(c: { displayName: string; kind: "residential" | "commercial"; firstName: string; lastName: string; company: string; email: string; phone: string } & QbAddress) {
  return {
    DisplayName: qbName(c.displayName),
    ...(c.kind === "commercial" ? { CompanyName: qbName(c.company || c.displayName) } : { GivenName: c.firstName.slice(0, 100), FamilyName: c.lastName.slice(0, 100) }),
    ...(c.email ? { PrimaryEmailAddr: { Address: c.email } } : {}),
    ...(c.phone ? { PrimaryPhone: { FreeFormNumber: c.phone } } : {}),
    ...(addr(c) ? { BillAddr: addr(c) } : {}),
  };
}

/** A job is a sub-customer of its customer, billed with the parent. */
export function subCustomerPayload(job: { qbName: string } & QbAddress, parentQbId: string) {
  return { DisplayName: qbName(job.qbName), Job: true, BillWithParent: true, ParentRef: ref(parentQbId), ...(addr(job) ? { ShipAddr: addr(job) } : {}) };
}

export function vendorPayload(v: { displayName: string; company: string; email: string; phone: string; is1099: boolean } & QbAddress) {
  return {
    DisplayName: qbName(v.displayName),
    ...(v.company ? { CompanyName: qbName(v.company) } : {}),
    ...(v.email ? { PrimaryEmailAddr: { Address: v.email } } : {}),
    ...(v.phone ? { PrimaryPhone: { FreeFormNumber: v.phone } } : {}),
    ...(addr(v) ? { BillAddr: addr(v) } : {}),
    Vendor1099: v.is1099,
  };
}

/** Quantity × rate when it's exact to the cent (QuickBooks checks Amount = Qty × UnitPrice); else 1 × the amount. */
const salesLine = (description: string, cents: number, itemId: string, qty?: { quantity?: number; unitPriceCents?: number }) => {
  const exact = qty?.quantity !== undefined && qty.unitPriceCents !== undefined && (BigInt(Math.round(qty.quantity * 10_000)) * BigInt(qty.unitPriceCents)) % 10_000n === 0n && lineAmountCents(qty.quantity, qty.unitPriceCents) === cents;
  return {
    Amount: qbAmount(cents),
    Description: description.slice(0, 4000),
    DetailType: "SalesItemLineDetail",
    SalesItemLineDetail: { ItemRef: ref(itemId), Qty: exact ? qty!.quantity! : 1, UnitPrice: qbAmount(exact ? qty!.unitPriceCents! : cents) },
  };
};

/** The signed contract as an Estimate on the job (spec §8: on signing). */
export function estimatePayload(e: { jobQbId: string; date: string; contractCents: number; description: string; itemId: string; docNumber?: string }) {
  return { CustomerRef: ref(e.jobQbId), TxnDate: e.date, ...(e.docNumber ? { DocNumber: e.docNumber.slice(0, 21) } : {}), Line: [salesLine(e.description, e.contractCents, e.itemId)] };
}

export function invoicePayload(i: { jobQbId: string; number: number; date: string; dueDate: string | null; lines: { description: string; amountCents: number; retainage?: boolean; quantity?: number; unitPriceCents?: number }[]; itemId: string; retainageItemId?: string; memo: string }) {
  return {
    CustomerRef: ref(i.jobQbId),
    DocNumber: String(i.number),
    TxnDate: i.date,
    ...(i.dueDate ? { DueDate: i.dueDate } : {}),
    ...(i.memo ? { CustomerMemo: { value: i.memo.slice(0, 1000) } } : {}),
    // Retainage held is a negative line to the retainage item; its release bills that item back.
    Line: i.lines.map((l) => salesLine(l.description, l.amountCents, l.retainage ? (i.retainageItemId ?? pending("retainage item")) : i.itemId, l)),
  };
}

/**
 * A payment applied to its invoice, or (one statement payment) to several: one line each. Card
 * surcharges aren't part of the invoices and aren't included.
 */
export function paymentPayload(p: { jobQbId: string; invoiceQbId?: string; amountCents?: number; applied?: { invoiceQbId: string; amountCents: number }[]; date: string; depositAccountId: string | null; reference: string; method: string }) {
  const applied = p.applied ?? [{ invoiceQbId: p.invoiceQbId!, amountCents: p.amountCents! }];
  const total = applied.reduce((n, a) => n + a.amountCents, 0);
  return {
    CustomerRef: ref(p.jobQbId),
    TotalAmt: qbAmount(total),
    TxnDate: p.date,
    ...(p.reference ? { PaymentRefNum: p.reference.slice(0, 21) } : {}),
    ...(p.depositAccountId ? { DepositToAccountRef: ref(p.depositAccountId) } : {}),
    PrivateNote: applied.length > 1 ? `CRM ${p.method} payment (statement, ${applied.length} invoices)` : `CRM ${p.method} payment`,
    Line: applied.map((a) => ({ Amount: qbAmount(a.amountCents), LinkedTxn: [{ TxnId: a.invoiceQbId, TxnType: "Invoice" }] })),
  };
}

/** One line of a bill or expense: its expense account (from the category) and job. */
export interface QbExpenseLine {
  amountCents: number;
  description: string;
  accountId: string;
  jobQbId: string | null;
}

const expenseLine = (l: QbExpenseLine) => ({
  Amount: qbAmount(l.amountCents),
  Description: l.description.slice(0, 4000),
  DetailType: "AccountBasedExpenseLineDetail",
  AccountBasedExpenseLineDetail: { AccountRef: ref(l.accountId), ...(l.jobQbId ? { CustomerRef: ref(l.jobQbId), BillableStatus: "NotBillable" } : {}) },
});

/** A bill to pay later, each line on its job (spec §10: line CustomerRef = job sub-customer). */
export function billPayload(b: { vendorQbId: string; date: string; dueDate: string | null; docNumber: string; memo: string; lines: QbExpenseLine[] }) {
  return {
    VendorRef: ref(b.vendorQbId),
    TxnDate: b.date,
    ...(b.dueDate ? { DueDate: b.dueDate } : {}),
    ...(b.docNumber ? { DocNumber: b.docNumber.slice(0, 21) } : {}),
    ...(b.memo ? { PrivateNote: b.memo.slice(0, 4000) } : {}),
    Line: b.lines.filter((l) => l.amountCents !== 0).map(expenseLine),
  };
}

/** A vendor credit (a credit memo, a return, an early-pay discount taken): like a bill, owed to us. */
export function vendorCreditPayload(b: { vendorQbId: string; date: string; docNumber: string; memo: string; lines: QbExpenseLine[] }) {
  const { DueDate: _due, ...rest } = billPayload({ ...b, dueDate: null }) as ReturnType<typeof billPayload> & { DueDate?: string };
  return rest;
}

/** An expense already paid (card, cash, check) from the payment account. */
export function purchasePayload(p: { vendorQbId: string | null; date: string; memo: string; lines: QbExpenseLine[]; paidFromAccountId: string; paymentType: "CreditCard" | "Cash" | "Check" }) {
  return {
    PaymentType: p.paymentType,
    AccountRef: ref(p.paidFromAccountId),
    TxnDate: p.date,
    ...(p.vendorQbId ? { EntityRef: { ...ref(p.vendorQbId), type: "Vendor" } } : {}),
    ...(p.memo ? { PrivateNote: p.memo.slice(0, 4000) } : {}),
    Line: p.lines.filter((l) => l.amountCents !== 0).map(expenseLine),
  };
}

export const pending = (what: string) => `pending:${what}`;
export const isPending = (id: string | null | undefined) => !id || id.startsWith("pending:");

export interface QbStatus {
  /** QuickBooks app keys are set up on this server. */
  configured: boolean;
  environment: "sandbox" | "production" | null;
  connected: boolean;
  companyName: string | null;
  connectedAt: string | null;
  /** Writes really go to QuickBooks (server switch); otherwise they're dry runs. */
  writesEnabled: boolean;
  lastError: string | null;
  redirectUri: string;
}

export interface QbOutboxItem {
  id: string;
  entity: string;
  recordId: string;
  label: string;
  payload: unknown;
  status: "dry_run" | "sent" | "failed" | "skipped";
  qbId: string | null;
  error: string | null;
  createdAt: string;
}

// ── Linking records that are already in QuickBooks ──

/** A name for matching: lower case, letters and digits only, without "Inc", "LLC", "The"... */
export function nameKey(name: string) {
  return name
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9 ]/g, " ")
    .replace(/\b(co|company|inc|incorporated|llc|l l c|corp|corporation|ltd|the)\b/g, " ")
    .replace(/\s+/g, "");
}

/** The words of a name in order, so "Smith, Pat" and "Pat Smith" match. */
const nameWords = (name: string) =>
  name
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
    .sort()
    .join(" ");

/** "Sendelbach Plumbing (Vendor)" → "Sendelbach Plumbing". */
const stripVendor = (name: string) => name.replace(/\s*\(vendor\)\s*$/i, "");
/** The ways a vendor's name is compared: as written, and its words in any order. */
const vendorKeys = (name: string) => {
  const n = stripVendor(name);
  return [nameKey(n), nameWords(n)].filter((k) => k.replace(/\s/g, "").length >= 3);
};

export interface QbCustomerRow {
  id: string;
  name: string;
  /** The parent customer, for a sub-customer (a job). */
  parentId: string | null;
  /** Its street in QuickBooks (ship-to, else bill-to), for matching jobs by address. */
  street?: string;
}

export interface QbMatchInput {
  /** Every contact, deleted ones too (their QuickBooks ids are taken). */
  contacts: { id: string; name: string; category: string; deleted: boolean; qbCustomerId: string | null; qbVendorId: string | null; /** The company and the person's name, for vendors filed under either. */ otherNames?: string[] }[];
  jobs: {
    id: string;
    number: number;
    customerId: string;
    deleted: boolean;
    qbSubCustomerId: string | null;
    /** JobNimbus's "Quickbooks ID", from the import. */
    jnQbId: string | null;
    street?: string;
    /** Its invoices' numbers as JobNimbus printed them (they went to QuickBooks with the same numbers). */
    invoiceNumbers?: string[];
    /** Its JobNimbus estimates' numbers (some QuickBooks jobs only have an estimate). */
    estimateNumbers?: string[];
    /** Its names: JobNimbus's job name, and its title here. */
    names?: string[];
    /**
     * It belongs in QuickBooks: it reached its workflow's QuickBooks stage (Sold) or later, or has
     * invoices. Earlier jobs (leads, estimates, lost) aren't expected there. Unset counts as yes.
     */
    needsQb?: boolean;
  }[];
  customers: QbCustomerRow[];
  vendors: { id: string; name: string }[];
  /** QuickBooks's invoices: number and the customer or job they're on. */
  qbInvoices?: { docNumber: string; customerId: string }[];
  /** QuickBooks's estimates, the same way. */
  qbEstimates?: { docNumber: string; customerId: string }[];
}

const DIRECTIONS: Record<string, string> = { north: "n", south: "s", east: "e", west: "w", northeast: "ne", northwest: "nw", southeast: "se", southwest: "sw" };
/**
 * A street, for matching: house number and the first word of the street name, ignoring directions
 * and spelling ("3425 N. Holman Ct" and "3425 north holman court" are both "3425 holman"). Null when
 * it doesn't start with a number.
 */
export function streetKey(street: string): string | null {
  const words = street.toLowerCase().replace(/[.,#]/g, " ").split(/\s+/).filter(Boolean);
  if (!words.length || !/^\d+[a-z]?$/.test(words[0]!)) return null;
  const name = words.slice(1).find((w) => !DIRECTIONS[w] && !Object.values(DIRECTIONS).includes(w));
  return name ? `${words[0]} ${name}` : null;
}

/** The street keys found anywhere in a QuickBooks name ("1693 - Smith - 3425 N Holman Ct - Roof"). */
function streetKeysIn(name: string): Set<string> {
  const out = new Set<string>();
  const words = name.toLowerCase().replace(/[.,#]/g, " ").split(/[\s-]+/).filter(Boolean);
  for (let i = 0; i < words.length; i++) {
    const k = /^\d+[a-z]?$/.test(words[i]!) ? streetKey(words.slice(i, i + 4).join(" ")) : null;
    if (k) out.add(k);
  }
  return out;
}

type JobCtx = {
  byId: Map<string, QbCustomerRow>;
  taken: Set<string>;
  /** Sub-customers by each number in their name. */
  byNumber: Map<number, QbCustomerRow[]>;
  byStreet: Map<string, QbCustomerRow[]>;
  byParent: Map<string, QbCustomerRow[]>;
  /** QuickBooks customers or jobs by invoice number, and by estimate number. */
  invoiceOwner: Map<string, Set<string>>;
  estimateOwner: Map<string, Set<string>>;
  /** Sub-customers by name. */
  byName: Map<string, QbCustomerRow[]>;
  /** Jobs not linked yet, per CRM customer. */
  openJobs: Map<string, number>;
};

function jobCtx(input: QbMatchInput, taken: Set<string>): JobCtx {
  const byId = new Map(input.customers.map((c) => [c.id, c]));
  const byNumber = new Map<number, QbCustomerRow[]>();
  const byStreet = new Map<string, QbCustomerRow[]>();
  const byParent = new Map<string, QbCustomerRow[]>();
  const add = <K>(m: Map<K, QbCustomerRow[]>, k: K, c: QbCustomerRow) => m.set(k, [...(m.get(k) ?? []), c]);
  for (const c of input.customers) {
    if (!c.parentId) continue;
    add(byParent, c.parentId, c);
    for (const n of new Set((c.name.match(/\d+/g) ?? []).map(Number))) add(byNumber, n, c);
    const keys = streetKeysIn(c.name);
    const own = c.street ? streetKey(c.street) : null;
    if (own) keys.add(own);
    for (const k of keys) add(byStreet, k, c);
  }
  const owners = (docs: { docNumber: string; customerId: string }[] | undefined) => {
    const m = new Map<string, Set<string>>();
    for (const i of docs ?? []) {
      const k = i.docNumber.trim().toLowerCase();
      if (k) m.set(k, (m.get(k) ?? new Set()).add(i.customerId));
    }
    return m;
  };
  const invoiceOwner = owners(input.qbInvoices);
  const estimateOwner = owners(input.qbEstimates);
  const byName = new Map<string, QbCustomerRow[]>();
  for (const c of input.customers) {
    const k = c.parentId ? jobNameKey(c.name) : "";
    if (k) add(byName, k, c);
  }
  const openJobs = new Map<string, number>();
  for (const j of input.jobs) if (!j.deleted && !j.qbSubCustomerId) openJobs.set(j.customerId, (openJobs.get(j.customerId) ?? 0) + 1);
  return { byId, taken, byNumber, byStreet, byParent, invoiceOwner, estimateOwner, byName, openJobs };
}

/** A job name, for matching: case, spacing, and punctuation aside ("NMT - Woods Chiropractic" = "nmt woods chiropractic"). */
function jobNameKey(name: string) {
  const k = name.toLowerCase().replace(/&/g, " and ").replace(/[^a-z0-9]+/g, " ").trim();
  return k.replace(/\s/g, "").length >= 4 ? k : "";
}

/** How strongly each kind of evidence points at a QuickBooks job. */
const WEIGHT = { invoices: 100, estimates: 90, jobnimbus: 80, name: 70, number: 50, street: 40, only: 20 } as const;
/** Evidence strong enough to stand on its own; two QuickBooks jobs with it is a conflict for a person to settle. */
const STRONG = 70;

/**
 * Where a job is in QuickBooks. Every piece of evidence is gathered for every QuickBooks job it points
 * at: its invoices' or estimates' numbers on that job; JobNimbus's link; the same job name; the job
 * number in the name; its street in the name or address; or the only QuickBooks job of a linked
 * customer with one job here. The QuickBooks job with the most wins, listing every reason. Two jobs
 * each with strong evidence is a conflict, left for a person. Everything but the invoice and estimate
 * numbers also needs the QuickBooks customer above it to be the job's customer: the one it's linked
 * to, or (not linked yet) one with the same name that no other contact has.
 */
function findJob(j: QbMatchInput["jobs"][number], owner: { name: string } | undefined, ownerQb: string | null, x: JobCtx): { qb: QbCustomerRow; how: string } | { reason: string } {
  const free = (c: QbCustomerRow) => !!c.parentId && !x.taken.has(c.id) && (ownerQb ? c.parentId === ownerQb : !x.taken.has(c.parentId));
  const sameName = (c: QbCustomerRow) => !!ownerQb || (!!owner && nameWords(x.byId.get(c.parentId!)?.name ?? "") === nameWords(owner.name));
  const evidence = new Map<string, { c: QbCustomerRow; score: number; reasons: string[] }>();
  const add = (c: QbCustomerRow | undefined, w: number, reason: string, needsCustomer = true) => {
    if (!c || !free(c) || (needsCustomer && !sameName(c))) return;
    const e = evidence.get(c.id) ?? { c, score: 0, reasons: [] };
    if (e.reasons.includes(reason)) return;
    e.score += w;
    e.reasons.push(reason);
    evidence.set(c.id, e);
  };
  const notes: string[] = [];
  const byDocs = (numbers: string[] | undefined, owners: Map<string, Set<string>>, w: number, what: string) => {
    const found = new Map<string, string[]>();
    for (const n of numbers ?? []) for (const id of owners.get(n.trim().toLowerCase()) ?? []) found.set(id, [...(found.get(id) ?? []), n]);
    for (const [id, nums] of found) {
      const c = x.byId.get(id);
      if (c && !c.parentId) notes.push(`Its ${what} ${nums.join(", ")} ${nums.length === 1 ? "is" : "are"} on "${c.name}" in QuickBooks, a customer rather than a job under one.`);
      else if (c && x.taken.has(c.id)) notes.push(`Its ${what} ${nums.join(", ")} ${nums.length === 1 ? "is" : "are"} on "${c.name}" in QuickBooks, which is already linked to another job or contact.`);
      add(c, w, `${what === "invoices" ? (nums.length === 1 ? "Invoice" : "Invoices") : nums.length === 1 ? "Estimate" : "Estimates"} ${nums.join(", ")}`, false);
    }
  };
  byDocs(j.invoiceNumbers, x.invoiceOwner, WEIGHT.invoices, "invoices");
  byDocs(j.estimateNumbers, x.estimateOwner, WEIGHT.estimates, "estimates");
  const jn = j.jnQbId ? x.byId.get(j.jnQbId) : undefined;
  add(jn, WEIGHT.jobnimbus, "JobNimbus's QuickBooks link", false);
  for (const n of new Set((j.names ?? []).map(jobNameKey).filter(Boolean))) for (const c of x.byName.get(n) ?? []) add(c, WEIGHT.name, "Same job name");
  for (const c of x.byNumber.get(j.number) ?? []) add(c, WEIGHT.number, "Job number in the name");
  const key = j.street ? streetKey(j.street) : null;
  if (key) for (const c of x.byStreet.get(key) ?? []) add(c, WEIGHT.street, "Same street address");
  if (ownerQb && x.openJobs.get(j.customerId) === 1) {
    const subs = (x.byParent.get(ownerQb) ?? []).filter(free);
    if (subs.length === 1) add(subs[0], WEIGHT.only, "The customer's only job on each side");
  }
  const ranked = [...evidence.values()].sort((a, b) => b.score - a.score);
  const strong = ranked.filter((e) => e.score >= STRONG);
  if (strong.length > 1) return { reason: `Points to ${strong.length} QuickBooks jobs: ${strong.map((e) => `"${e.c.name}" (${e.reasons.join(", ").toLowerCase()})`).join("; ")}. Link the right one by hand.` };
  if (ranked.length > 1 && !strong.length && ranked[0]!.score === ranked[1]!.score) return { reason: `${ranked.length} QuickBooks jobs fit equally: ${ranked.map((e) => `"${e.c.name}" (${e.reasons.join(", ").toLowerCase()})`).join("; ")}. Link it by hand.` };
  if (ranked.length) return { qb: ranked[0]!.c, how: ranked[0]!.reasons.join(" · ") };
  if (notes.length) return { reason: `${notes.join(" ")} Link it by hand.` };
  if (j.jnQbId && !jn) return { reason: `JobNimbus links it to QuickBooks #${j.jnQbId}, which isn't in this QuickBooks company. Link it by hand.` };
  // Close, but under someone else.
  const near = (x.byNumber.get(j.number) ?? []).filter((c) => !x.taken.has(c.id));
  if (near.length) return { reason: `${near.length === 1 ? "A QuickBooks job has" : `${near.length} QuickBooks jobs have`} job number ${j.number} in the name (${near.map((c) => `"${c.name}" under "${x.byId.get(c.parentId!)?.name ?? "?"}"`).join(", ")}), but not under ${owner?.name ?? "this job's customer"}. Link it by hand if it's this job.` };
  return { reason: `Not found by invoice or estimate numbers, JobNimbus's link, job name, job number, or address.${j.invoiceNumbers?.length || j.estimateNumbers?.length ? "" : " It has no JobNimbus invoices or estimates here to look up."} Link it by hand.` };
}

export interface QbMatch {
  kind: "job" | "customer" | "vendor";
  recordId: string;
  /** The CRM side: "Job #1043" or the contact's name. */
  label: string;
  qbId: string;
  qbName: string;
  /** Why they match, in plain words. */
  how: string;
}

/**
 * Proposes links from CRM records not yet in QuickBooks to QuickBooks records already there, so the
 * sync uses them instead of making duplicates. Jobs first (JobNimbus's link, else the job number at
 * the start of a sub-customer's name), then their customers (the sub-customer's parent), then
 * customers and vendors by name. Only one-to-one matches; a QuickBooks record is never used twice.
 */
export function matchQbRecords(input: QbMatchInput): QbMatch[] {
  const out: QbMatch[] = [];
  const takenCustomer = new Set([...input.contacts.map((c) => c.qbCustomerId), ...input.jobs.map((j) => j.qbSubCustomerId)].filter((x): x is string => !!x));
  const takenVendor = new Set(input.contacts.map((c) => c.qbVendorId).filter((x): x is string => !!x));
  const byId = new Map(input.customers.map((c) => [c.id, c]));
  const contactById = new Map(input.contacts.map((c) => [c.id, c]));
  const linkedContact = new Set<string>();

  const ctx = jobCtx(input, takenCustomer);

  const linkCustomer = (contactId: string, qb: QbCustomerRow, how: string) => {
    const c = contactById.get(contactId);
    if (!c || c.deleted || c.qbCustomerId || linkedContact.has(c.id) || takenCustomer.has(qb.id)) return;
    takenCustomer.add(qb.id);
    linkedContact.add(c.id);
    out.push({ kind: "customer", recordId: c.id, label: c.name, qbId: qb.id, qbName: qb.name, how });
  };

  for (const j of [...input.jobs].sort((a, b) => a.number - b.number)) {
    if (j.deleted || j.qbSubCustomerId) continue;
    // The sub-customer must belong to this job's customer: the one it's already linked to, or (if
    // not linked yet) a parent no other customer has.
    const owner = contactById.get(j.customerId);
    const ownerQb = owner?.qbCustomerId ?? out.find((m) => m.kind === "customer" && m.recordId === j.customerId)?.qbId ?? null;
    const found = findJob(j, owner, ownerQb, ctx);
    if (!("qb" in found)) continue;
    const { qb, how } = found;
    takenCustomer.add(qb.id);
    out.push({ kind: "job", recordId: j.id, label: `Job #${j.number}`, qbId: qb.id, qbName: qb.name, how });
    const parent = byId.get(qb.parentId!);
    if (parent) linkCustomer(j.customerId, parent, `Customer of job #${j.number} in QuickBooks`);
  }

  const groupByKey = <T extends { name: string }>(rows: T[]) => {
    const m = new Map<string, T[]>();
    for (const r of rows) {
      const k = nameKey(r.name);
      if (k.length >= 3) m.set(k, [...(m.get(k) ?? []), r]);
    }
    return m;
  };
  const topCustomers = groupByKey(input.customers.filter((c) => !c.parentId));
  const customerContacts = groupByKey(input.contacts.filter((c) => c.category === "customer" && !c.deleted && !c.qbCustomerId && !linkedContact.has(c.id)));
  for (const [k, contacts] of customerContacts) {
    const qb = (topCustomers.get(k) ?? []).filter((c) => !takenCustomer.has(c.id));
    if (contacts.length === 1 && qb.length === 1) linkCustomer(contacts[0]!.id, qb[0]!, "Same name");
  }

  // Vendors: by the contact's name, then by the company or the person ("Sendelbach Plumbing" or
  // "Stan Sendelbach", either order). A "(Vendor)" on the QuickBooks name is ignored.
  const vendorContacts = input.contacts.filter((c) => (c.category === "subcontractor" || c.category === "vendor") && !c.deleted && !c.qbVendorId);
  const linkedVendor = new Set<string>();
  for (const pass of [(c: (typeof vendorContacts)[number]) => [c.name], (c: (typeof vendorContacts)[number]) => [c.name, ...(c.otherNames ?? [])]]) {
    const hits = new Map<string, Set<string>>(); // QuickBooks vendor → contacts that match it
    const of = new Map<string, Set<string>>(); // contact → QuickBooks vendors it matches
    for (const c of vendorContacts) {
      if (linkedVendor.has(c.id)) continue;
      const keys = new Set(pass(c).flatMap(vendorKeys));
      for (const v of input.vendors) {
        if (takenVendor.has(v.id) || !vendorKeys(v.name).some((k) => keys.has(k))) continue;
        hits.set(v.id, (hits.get(v.id) ?? new Set()).add(c.id));
        of.set(c.id, (of.get(c.id) ?? new Set()).add(v.id));
      }
    }
    for (const c of vendorContacts) {
      const vs = [...(of.get(c.id) ?? [])];
      if (vs.length !== 1 || hits.get(vs[0]!)!.size !== 1 || takenVendor.has(vs[0]!)) continue;
      const v = input.vendors.find((x) => x.id === vs[0])!;
      takenVendor.add(v.id);
      linkedVendor.add(c.id);
      out.push({ kind: "vendor", recordId: c.id, label: c.name, qbId: v.id, qbName: v.name, how: nameKey(stripVendor(v.name)) === nameKey(c.name) ? "Same name" : "Same company or person" });
    }
  }
  return out;
}

export const qbLinkSchema = z.object({ links: z.array(z.object({ kind: z.enum(["job", "customer", "vendor"]), recordId: z.string().uuid(), qbId: z.string().min(1).max(40) })).min(1).max(5000) });

/** One kind's linking status: how many are linked, ready to link (in Find matches), and not found. */
export interface QbLinkCounts {
  total: number;
  linked: number;
  ready: number;
  notFound: number;
  /** Jobs: not linked and not expected in QuickBooks yet (before Sold). Not in notFound. */
  notNeeded?: number;
}
export interface QbUnlinked {
  kind: "job" | "customer" | "vendor";
  recordId: string;
  label: string;
  reason: string;
  /** Who it is, from what JobNimbus brought in: a job's name, address, and stage; a contact's address, phone, and email. */
  detail?: string;
}
export interface QbLinkStatus {
  jobs: QbLinkCounts;
  customers: QbLinkCounts;
  vendors: QbLinkCounts;
  /** Not linked and not ready to link, each with why (at most 1,000). */
  unlinked: QbUnlinked[];
}

/**
 * How linking stands, and why each record that isn't linked couldn't be matched (same rules as
 * matchQbRecords). Deleted records and contacts that aren't customers, subs, or vendors don't count.
 */
export function qbLinkStatus(input: QbMatchInput, matches: QbMatch[]): QbLinkStatus {
  const ready = new Set(matches.map((m) => `${m.kind}:${m.recordId}`));
  const byId = new Map(input.customers.map((c) => [c.id, c]));
  const contactById = new Map(input.contacts.map((c) => [c.id, c]));
  const jobByQb = new Map(input.jobs.filter((j) => j.qbSubCustomerId).map((j) => [j.qbSubCustomerId!, j]));
  const contactByQb = new Map(input.contacts.filter((c) => c.qbCustomerId).map((c) => [c.qbCustomerId!, c]));
  const vendorTaken = new Map(input.contacts.filter((c) => c.qbVendorId).map((c) => [c.qbVendorId!, c]));
  const taken = new Set([...input.contacts.map((c) => c.qbCustomerId), ...input.jobs.map((j) => j.qbSubCustomerId)].filter((x): x is string => !!x));
  const ctx = jobCtx(input, taken);
  const unlinked: QbUnlinked[] = [];
  const counts = (): QbLinkCounts => ({ total: 0, linked: 0, ready: 0, notFound: 0 });
  const jobs = counts();
  const customers = counts();
  const vendors = counts();
  const tally = (c: QbLinkCounts, linked: boolean, key: string, miss: () => QbUnlinked) => {
    c.total++;
    if (linked) c.linked++;
    else if (ready.has(key)) c.ready++;
    else {
      c.notFound++;
      if (unlinked.length < 1000) unlinked.push(miss());
    }
  };

  jobs.notNeeded = 0;
  customers.notNeeded = 0;
  for (const j of input.jobs) {
    if (j.deleted) continue;
    if (!j.qbSubCustomerId && j.needsQb === false) {
      jobs.notNeeded!++;
      continue;
    }
    tally(jobs, !!j.qbSubCustomerId, `job:${j.id}`, () => {
      const label = `Job #${j.number} ${contactById.get(j.customerId)?.name ?? ""}`.trim();
      const why = (reason: string): QbUnlinked => ({ kind: "job", recordId: j.id, label, reason });
      const owner = contactById.get(j.customerId);
      if (j.jnQbId) {
        const sub = byId.get(j.jnQbId);
        if (sub && jobByQb.get(sub.id)) return why(`Its QuickBooks job "${sub.name}" (JobNimbus's link) is already linked to job #${jobByQb.get(sub.id)!.number}.`);
        if (sub?.parentId && owner?.qbCustomerId && sub.parentId !== owner.qbCustomerId) return why(`JobNimbus links it to "${sub.name}", which is under "${byId.get(sub.parentId)?.name ?? "another customer"}" in QuickBooks, not ${owner.name}.`);
      }
      const found = findJob(j, owner, owner?.qbCustomerId ?? null, ctx);
      return why("reason" in found ? found.reason : `Matches "${found.qb.name}" (${found.how.toLowerCase()}), but its customer is linked to someone else. Link it by hand.`);
    });
  }
  for (const c of input.contacts) {
    if (c.deleted) continue;
    if (c.category === "customer") {
      // Like jobs: a customer belongs in QuickBooks once one of their jobs does (Sold or later, or invoiced).
      if (!c.qbCustomerId && !input.jobs.some((j) => !j.deleted && j.customerId === c.id && (j.qbSubCustomerId || j.needsQb !== false))) {
        customers.notNeeded = (customers.notNeeded ?? 0) + 1;
        continue;
      }
      tally(customers, !!c.qbCustomerId, `customer:${c.id}`, () => {
        const same = input.customers.filter((q) => !q.parentId && nameKey(q.name) === nameKey(c.name));
        const free = same.filter((q) => !contactByQb.has(q.id));
        const reason = !same.length
          ? "No QuickBooks customer with this name (and no linked job to find it by)."
          : !free.length
            ? `QuickBooks "${same[0]!.name}" is already linked to ${contactByQb.get(same[0]!.id)?.name ?? "another contact"}.`
            : `${free.length} QuickBooks customers have this name.`;
        return { kind: "customer", recordId: c.id, label: c.name, reason };
      });
    } else if (c.category === "subcontractor" || c.category === "vendor") {
      tally(vendors, !!c.qbVendorId, `vendor:${c.id}`, () => {
        const keys = new Set([c.name, ...(c.otherNames ?? [])].flatMap(vendorKeys));
        const same = input.vendors.filter((v) => vendorKeys(v.name).some((k) => keys.has(k)));
        const free = same.filter((v) => !vendorTaken.has(v.id));
        const reason = !same.length ? "No QuickBooks vendor with this name or company. Link one on their contact page." : !free.length ? `QuickBooks "${same[0]!.name}" is already linked to ${vendorTaken.get(same[0]!.id)?.name ?? "another contact"}.` : `${free.length} QuickBooks vendors have this name.`;
        return { kind: "vendor", recordId: c.id, label: c.name, reason };
      });
    }
  }
  return { jobs, customers, vendors, unlinked };
}
